import {
  useState,
  useEffect,
  useCallback,
  useRef,
  memo,
  type FC,
  type ReactNode
} from 'react';
import { renderAsync } from 'docx-preview';
import { Spin } from 'antd';
import clsx from 'clsx';
import { BACKEND_URL } from '../../constants/apollo';
import { AUTH_KEY } from '../../constants/storageKeys';
import { useDownloadContent } from '../../services/content';
import { openDownloadedContent } from '../../utils/openDownloadedContent';
import './Document.scss';

interface Props {
  canDownload: boolean
  contentId?: string
  fileKey: string
  name: string
  header?: ReactNode
  onFallbackToPdf: () => void
}

const DOCX_SIGNATURE = [0x50, 0x4B];

const isDocxBlob = async (blob: Blob): Promise<boolean> => {
  const header = new Uint8Array(await blob.slice(0, 2).arrayBuffer());
  return header[0] === DOCX_SIGNATURE[0] && header[1] === DOCX_SIGNATURE[1];
};

const isPdfResponse = (contentType: string): boolean => contentType.includes('pdf');

const getSourceUrls = (fileKey: string): string[] => {
  const encodedKey = encodeURIComponent(fileKey);
  return [
    `${BACKEND_URL}/documents/source/${encodedKey}`,
    `${BACKEND_URL}/documents/view/${encodedKey}?source=true`,
  ];
};

const WordDocumentViewer: FC<Props> = ({
  canDownload,
  contentId,
  fileKey,
  name,
  header,
  onFallbackToPdf,
}) => {
  const { downloadContent, loading: downloading } = useDownloadContent();
  const viewerRef = useRef<HTMLDivElement>(null);

  const [scale, setScale] = useState(1);
  const [docxBlob, setDocxBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setRendering(false);
      setError(null);
      setDocxBlob(null);

      try {
        const token = localStorage.getItem(AUTH_KEY) ?? '';

        for (const url of getSourceUrls(fileKey)) {
          const response = await fetch(url, { headers: { accessToken: token } });
          if (!response.ok) continue;

          const contentType = response.headers.get('content-type') ?? '';
          const blob = await response.blob();

          if (isPdfResponse(contentType) || !(await isDocxBlob(blob))) {
            if (!cancelled) onFallbackToPdf();
            return;
          }

          if (!cancelled) {
            setDocxBlob(blob);
            return;
          }
        }

        if (!cancelled) onFallbackToPdf();
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Error al cargar el documento');
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [fileKey, onFallbackToPdf]);

  useEffect(() => {
    if (!docxBlob || !viewerRef.current) return;

    let cancelled = false;
    const container = viewerRef.current;

    const render = async () => {
      setRendering(true);
      container.innerHTML = '';

      try {
        await renderAsync(docxBlob, container, undefined, {
          breakPages: true,
          className: 'crossfactory-docx',
          ignoreFonts: false,
          ignoreHeight: false,
          ignoreLastRenderedPageBreak: true,
          ignoreWidth: false,
          inWrapper: true,
          trimXmlDeclaration: true,
          useBase64URL: true,
        });
      } catch (e) {
        if (!cancelled) {
          onFallbackToPdf();
        }
      } finally {
        if (!cancelled) {
          setRendering(false);
          setLoading(false);
        }
      }
    };

    void render();

    return () => {
      cancelled = true;
      container.innerHTML = '';
    };
  }, [docxBlob, onFallbackToPdf]);

  const handleDownload = useCallback(async () => {
    if (!canDownload || !contentId) return;

    const response = await downloadContent(contentId);
    openDownloadedContent(response);
  }, [canDownload, contentId, downloadContent]);

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    if (!canDownload) {
      event.preventDefault();
    }
  }, [canDownload]);

  if (error) {
    return (
      <div className="crossfactory-document__state">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div
      className={clsx('crossfactory-document', 'notranslate', !canDownload && 'no-download')}
      onContextMenu={handleContextMenu}
      translate="no"
    >
      <div className="crossfactory-document__header">
        {header}
        <div className="crossfactory-document__toolbar">
          <span className="crossfactory-document__page-info">{name}</span>
          <button
            type="button"
            className="crossfactory-document__btn cursor-pointer"
            onClick={() => setScale((s) => Math.max(0.6, Number((s - 0.1).toFixed(1))))}
          >
            −
          </button>
          <span className="crossfactory-document__page-info">{Math.round(scale * 100)}%</span>
          <button
            type="button"
            className="crossfactory-document__btn cursor-pointer"
            onClick={() => setScale((s) => Math.min(1.8, Number((s + 0.1).toFixed(1))))}
          >
            +
          </button>
          {canDownload && contentId ? (
            <button
              type="button"
              className="crossfactory-document__btn crossfactory-document__download cursor-pointer"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? 'Descargando...' : 'Descargar'}
            </button>
          ) : null}
        </div>
      </div>

      <div className="crossfactory-document__viewer crossfactory-document__viewer--word">
        {(loading || rendering) ? (
          <div className="crossfactory-document__state crossfactory-document__state--overlay">
            <Spin size="large" tip="Cargando documento..." />
          </div>
        ) : null}
        <div
          className="crossfactory-document__word-scale"
          style={{ transform: `scale(${scale})`, transformOrigin: 'top center' }}
        >
          <div ref={viewerRef} className="crossfactory-document__word-content" />
        </div>
      </div>
    </div>
  );
};

const WordDocument = memo(WordDocumentViewer);

export { WordDocument };
